/*
 * COPYRIGHT Motorola Solutions, INC.
 * ALL RIGHTS RESERVED.
 * MOTOROLA SOLUTIONS CONFIDENTIAL RESTRICTED
 */

import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { GenericStompConnection } from './generic-stomp-connection';
import { distinctUntilChanged, filter, take, takeUntil } from 'rxjs/operators';
import { RxStompState } from '@stomp/rx-stomp';
import { selectClustersMapWithToken } from '../../../user/+state/user.selectors';
import { updateStompConnectionMultipleWebsocket } from '../../../call/+state/media.actions';
import { Topic } from '../../../user/model/multiple-subscriptions';
import { selectWebsocketConnectionStatusOverride } from '../../../call/+state/media.selectors';
import { selectApplicationPresence} from '../../../user/+state/user.selectors';
import { StompBaseConsumer } from '../../abstract/stomp-base-consumer';
import { loggedAtDifferentPosition } from '../../../configuration/+state/configuration.actions';

@Injectable({
  providedIn: 'root'
})
export class StompManagerService implements OnDestroy {
    private unsubscribe$ = new Subject<void>();
    private connections = new Map<string, GenericStompConnection>();

  constructor(private store: Store) {
    this.store.select(selectClustersMapWithToken)
      .pipe(
        filter(({
                  clustersAddressMap,
                  token
                }) => !!clustersAddressMap && Object.keys(clustersAddressMap).length > 0 && !!token)
      )
      .subscribe(({ clustersAddressMap, token }) => {
        for (const [cluster, url] of Object.entries(clustersAddressMap)) {
          this.addConnection(url, token, cluster);
        }
      });
  }

  addConnection(url: string, token: string, cluster: string): void {
    if (this.connections.has(cluster)) {
      console.warn(`Connection for URL ${url} already exists.`);
      this.connections.get(cluster).updateToken(token);
      return;
    }

        const connection = new GenericStompConnection(url, token);
        connection.stompErrors$.pipe(takeUntil(this.unsubscribe$)).subscribe((frame) => {
            console.error(`STOMP error on cluster ${cluster}:`, frame.headers.message);
            const errorMessage = frame.headers.message;
            if (errorMessage.includes('User already logged in from another position')) {
                const match = errorMessage.match(/\s*<<(\d+)>>\s*<<([\W\w\s]*)>>/);
                const position = match ? match[1] : " ";
                const positionName = match ? match[2] : null;

                // immediately deactivate failed websocket to this specific cluster
                this.connections.get(cluster).disconnectWebSocket();

                // check if we're currently logged in and if not, deactivate all websockets
                this.store
                    .select(selectApplicationPresence)
                    .pipe(
                        filter(({loggedIn}) => !loggedIn),
                        take(1)
                    )
                    .subscribe(() => {
                        this.connections.forEach((connection, url) => connection.disconnectWebSocket());
                        this.store.dispatch(loggedAtDifferentPosition({ positionNumber: position, positionName: positionName }));
                    });
            }
        });

    connection.connectionState$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((connectionState) => {
        this.store.dispatch(updateStompConnectionMultipleWebsocket({
          rxStompState: connectionState,
          clusterName: cluster
        }));
      });
    connection.webSocketErrors$.subscribe((error) => {
      console.error(`WebSocket error ${cluster}:`, error);
    });
    connection.setConnection();
    combineLatest(
      connection.connectionState$,
      this.store.select(selectWebsocketConnectionStatusOverride(cluster))
    ).pipe(
      filter(([connectionState, overrideState]) => overrideState !== undefined),
      takeUntil(this.unsubscribe$)
    ).subscribe(([connectionState, overrideState]) => {
      if (overrideState && connectionState !== RxStompState.OPEN && connectionState !== RxStompState.CONNECTING) {
        connection.connectWebSocket();
      } else if (connectionState === RxStompState.OPEN && !overrideState) {
        connection.disconnectWebSocket();
      }
    });
    this.connections.set(cluster, connection);
  }

    watchAsync(topic: Topic, receiptId: string, stompConsumer: StompBaseConsumer, clusterName: string, clusterLabel: string): any {
        return this.subscribeConnectionToTopic(topic, receiptId, stompConsumer, clusterName, clusterLabel).pipe(
            takeUntil(this.unsubscribe$)
        );
    }

    subscribeConnectionToTopic(topic: Topic, receiptId: string, stompConsumer: StompBaseConsumer, clusterName: string, clusterLabel: string) {
        const connection = this.connections.get(clusterName);
        const receipt = { receipt: receiptId, id: topic.replace('/', '').replaceAll('/', '-') };

        connection.connected$.pipe(takeUntil(this.unsubscribe$)).subscribe(() =>
          connection.asyncReceipt(receiptId).then(() => {
              stompConsumer.handleSubscriptionInit(topic, receiptId, clusterName, clusterLabel);
          })
        );

        connection.connectionState$
            .pipe(distinctUntilChanged(), filter((state) => state === RxStompState.CLOSED)).subscribe(() => {
                stompConsumer.handleSubscriptionEnd(topic, receiptId, clusterName, clusterLabel);
            });

        connection.stompErrors$.subscribe((frame) => {
            stompConsumer.handleStompError(topic, receiptId, frame.body, clusterName, clusterLabel);
        });
        const stompHeaders = { receipt: receipt.receipt, id: receipt.id };
        return connection.watch(topic, stompHeaders);
    }

  cleanUp(): void {
    this.connections.forEach((connection, url) => {
      connection.disconnectWebSocket();
      console.info(`Connection to ${url} cleaned up.`);
    });
    this.connections.clear();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.cleanUp();
  }
}
